import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ChevronRight, ChevronLeft, Camera, Menu, X } from 'lucide-react'
import { useCallback } from 'react'

export function PresentationNavigation({ xyzAPI, thumbnails = {} })
{
    const [ currentZoneIndex, setCurrentZoneIndex ] = useState(0)
    const [ waypoints, setWaypoints ] = useState([])
    const [ isSidebarVisible, setIsSidebarVisible ] = useState(false)

    // Load waypoints and subscribe to changes
    useEffect(() =>
    {
        if (!xyzAPI) return
        const sceneManager = xyzAPI.getSceneManager()
        if (sceneManager)
        {
            const wp = sceneManager.waypoints || []
            setWaypoints(wp)
        }

        // Subscribe to camera/waypoint changes
        if (xyzAPI.subscribe)
        {
            const unsubscribe = xyzAPI.subscribe((waypoint) =>
            {
                if (waypoint && typeof waypoint.index === 'number')
                {
                    setCurrentZoneIndex(waypoint.index)
                }
            })
            return () => unsubscribe()
        }
    }, [ xyzAPI ])

    const goNext = useCallback(() =>
    {
        if (!xyzAPI || waypoints.length === 0) return
        const nextIndex = (currentZoneIndex + 1) % waypoints.length
        xyzAPI.goToSceneZoneByIndex(nextIndex, false)
        setCurrentZoneIndex(nextIndex)
    }, [ xyzAPI, waypoints, currentZoneIndex ])

    const goPrevious = useCallback(() =>
    {
        if (!xyzAPI || waypoints.length === 0) return
        const prevIndex = (currentZoneIndex - 1 + waypoints.length) % waypoints.length
        xyzAPI.goToSceneZoneByIndex(prevIndex, false)
        setCurrentZoneIndex(prevIndex)
    }, [ xyzAPI, waypoints, currentZoneIndex ])

    const goToIndex = useCallback((index) =>
    {
        if (!xyzAPI) return
        xyzAPI.goToSceneZoneByIndex(index, false)
        setCurrentZoneIndex(index)
    }, [ xyzAPI ])

    // Keyboard navigation
    useEffect(() =>
    {
        const handleKeyDown = (e) =>
        {
            if (e.key === 'ArrowLeft') goPrevious()
            if (e.key === 'ArrowRight') goNext()
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [ goNext, goPrevious ])

    if (!xyzAPI || waypoints.length === 0) return null



    return (
        <>
            {/* Sidebar Toggle Button */}
            <button
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                className="fixed top-4 left-4 z-[60] p-2 rounded-full bg-black/50 hover:bg-black/80 text-white backdrop-blur-sm border border-white/10 transition-colors pointer-events-auto"
            >
                {isSidebarVisible ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Sidebar Index Container */}
            <div className={`group fixed left-0 top-0 bottom-0 z-50 flex pointer-events-none ${!isSidebarVisible ? '-translate-x-full' : ''} transition-transform duration-300`}>

                {/* The Sidebar Content */}
                <div
                    className="
                        w-64 h-full bg-black/90 backdrop-blur-md border-r border-white/10 
                        flex flex-col pointer-events-auto
                    "
                    onWheel={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onPointerMove={(e) => e.stopPropagation()}
                >
                    <div className="p-4 pl-16 border-b border-white/10 flex justify-between items-center">
                        <h2 className="text-white text-xs font-bold tracking-widest uppercase opacity-70">Slides</h2>
                        <span className="text-xs text-white/50">{currentZoneIndex + 1} / {waypoints.length}</span>
                    </div>

                    {/* Scrollable List */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                        {waypoints.map((wp, i) =>
                        {
                            const isActive = i === currentZoneIndex
                            const name = wp.camera?.anchor?.name || `Side ${i + 1}`

                            return (
                                <div
                                    key={i}
                                    className={`
                                        relative cursor-pointer rounded-lg overflow-hidden border transition-all duration-200
                                        ${isActive ? 'border-blue-500 ring-1 ring-blue-500/50' : 'border-white/10 hover:border-white/30'}
                                    `}
                                    onClick={() => goToIndex(i)}
                                >
                                    {/* Thumbnail Area */}
                                    <div className="aspect-video bg-gray-800 relative group-hover:bg-gray-700 transition-colors">
                                        {thumbnails && thumbnails[ i ] ? (
                                            <img
                                                src={thumbnails[ i ]}
                                                alt={name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            /* Placeholder visualization */
                                            <div className="absolute inset-0 flex items-center justify-center text-white/20">
                                                <Camera size={24} />
                                            </div>
                                        )}

                                        {/* Number Badge */}
                                        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-xs text-white font-mono border border-white/10">
                                            {i + 1}
                                        </div>
                                    </div>

                                    {/* Label */}
                                    <div className="p-2 bg-white/5">
                                        <p className={`text-xs truncate ${isActive ? 'text-white' : 'text-gray-400'}`}>
                                            {name}
                                        </p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {/* Bottom Navigation Arrows */}
                    <div className="p-4 border-t border-white/10 grid grid-cols-2 gap-2 bg-black/20">
                        <button
                            onClick={(e) => { e.stopPropagation(); goPrevious(); }}
                            className="flex items-center justify-center gap-2 p-3 rounded bg-white/5 hover:bg-white/10 hover:text-white text-gray-400 transition-colors border border-white/5"
                        >
                            <ChevronLeft size={16} />
                            <span className="text-xs font-medium">Prev</span>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                            className="flex items-center justify-center gap-2 p-3 rounded bg-white/5 hover:bg-white/10 hover:text-white text-gray-400 transition-colors border border-white/5"
                        >
                            <span className="text-xs font-medium">Next</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Prev Button (Left Side) */}
            <div
                className={`absolute top-1/2 left-8 -translate-y-1/2 z-40 group cursor-pointer`}
                onClick={goPrevious}
            >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm border border-white/20 transition-all duration-300 hover:bg-black/80 hover:scale-110 active:scale-95">
                    <ChevronLeft className="w-6 h-6 text-white opacity-70 group-hover:opacity-100" />
                </div>
            </div>

            {/* Next Button (Right Side) */}
            <div
                className={`absolute top-1/2 right-8 -translate-y-1/2 z-40 group cursor-pointer`}
                onClick={goNext}
            >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm border border-white/20 transition-all duration-300 hover:bg-black/80 hover:scale-110 active:scale-95">
                    <ChevronRight className="w-6 h-6 text-white opacity-70 group-hover:opacity-100" />
                </div>
            </div>
        </>
    )
}

PresentationNavigation.propTypes = {
    xyzAPI: PropTypes.object,
    thumbnails: PropTypes.object
}
